import React from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Tabs, Tab, Chip } from '@heroui/react';

const GoogleDorkingModal = ({ isOpen, onClose, dorkingData }) => {

  if (!dorkingData || !dorkingData.site_results) {
    return null;
  }

  const handleCopyAllUrls = () => {
    const allUrls = [];
    dorkingData.site_results.forEach(site => {
      if (site.searchStrategies) {
        site.searchStrategies.forEach(strategy => {
          allUrls.push(strategy.searchUrl);
        });
      }
      if (site.searchUrl) {
        allUrls.push(site.searchUrl);
      }
    });
    navigator.clipboard.writeText(allUrls.join('\n'));
  };

  const handleCopySiteUrls = (site) => {
    const urls = [];
    if (site.searchStrategies) {
      site.searchStrategies.forEach(strategy => {
        urls.push(strategy.searchUrl);
      });
    }
    if (site.searchUrl) {
      urls.push(site.searchUrl);
    }
    navigator.clipboard.writeText(urls.join('\n'));
  };

  const getSiteIcon = (siteName) => {
    const icons = {
      'x.com': '❌', // X logo
      'twitter.com': '🐦', // Legacy support
      'facebook.com': '📘',
      'instagram.com': '📷',
      'linkedin.com': '💼',
      'github.com': '🐙',
      'reddit.com': '🤖',
      'youtube.com': '📺',
      'tiktok.com': '🎵'
    };
    return icons[siteName] || '🌐';
  };

  const getEffectivenessColor = (effectiveness) => {
    switch (effectiveness) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'low': return 'danger';
      default: return 'default';
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      size="5xl"
      scrollBehavior="inside"
      classNames={{
        base: "max-h-[90vh]",
        body: "p-6",
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🎯</span>
                  <div>
                    <h2 className="text-xl font-bold">{dorkingData.tool_used}</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Query: "{dorkingData.query}" • {dorkingData.total_sites} sites • {dorkingData.total_results} results
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {dorkingData.tool_used?.includes('Enhanced') && (
                    <Chip size="sm" variant="flat" color="primary">🚀 Enhanced</Chip>
                  )}
                  <Button
                    size="sm"
                    variant="flat"
                    color="primary"
                    startContent={<span>📋</span>}
                    onClick={handleCopyAllUrls}
                  >
                    Copy All URLs
                  </Button>
                </div>
              </div>

              {/* Search Variations */}
              {dorkingData.search_variations && (
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
                    🔍 Search Variations Generated:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {dorkingData.search_variations.map((variation, idx) => (
                      <Chip key={idx} size="sm" variant="flat" color="primary">
                        {variation}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}

              {/* Advanced Features */}
              {dorkingData.advanced_features && (
                <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-sm font-medium text-green-700 dark:text-green-300 mb-2">
                    ⚡ Advanced Features:
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {dorkingData.advanced_features.map((feature, idx) => (
                      <div key={idx} className="text-sm text-green-600 dark:text-green-400 flex items-start gap-1">
                        <span>•</span> {feature}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </ModalHeader>

            <ModalBody>
              <Tabs 
                aria-label="Site Results"
                variant="underlined"
                classNames={{
                  tabList: "gap-6 w-full relative rounded-none p-0 border-b border-divider",
                  cursor: "w-full bg-primary",
                  tab: "max-w-fit px-0 h-12",
                  tabContent: "group-data-[selected=true]:text-primary"
                }}
              >
                {dorkingData.site_results.map((site, siteIdx) => (
                  <Tab 
                    key={siteIdx} 
                    title={
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getSiteIcon(site.site)}</span>
                        <div className="flex flex-col items-start">
                          <span className="text-sm font-medium">{site.site}</span>
                          <div className="flex items-center gap-1">
                            {site.directVerification?.found ? '✅' : site.found ? '✓' : '○'}
                            <span className="text-xs text-gray-500">
                              {site.searchStrategies?.length || 0} strategies
                            </span>
                          </div>
                        </div>
                      </div>
                    }
                  >
                    <div className="space-y-4">
                      {/* Site Header */}
                      <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{getSiteIcon(site.site)}</span>
                          <div>
                            <h3 className="font-bold text-lg">{site.site}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              {site.confidence && (
                                <Chip size="sm" variant="flat" color={
                                  site.confidence === 'high' ? 'success' : 
                                  site.confidence === 'medium' ? 'warning' : 'danger'
                                }>
                                  {site.confidence} confidence
                                </Chip>
                              )}
                              {site.directCheckPossible && (
                                <Chip size="sm" variant="flat" color="success">Direct Verification</Chip>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="flat"
                          color="primary"
                          startContent={<span>📋</span>}
                          onClick={() => handleCopySiteUrls(site)}
                        >
                          Copy Site URLs
                        </Button>
                      </div>

                      {/* Direct Verification Results */}
                      {site.directVerification && (
                        <div className={`p-4 rounded-lg border-l-4 ${
                          site.directVerification.found 
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-500' 
                            : 'bg-orange-50 dark:bg-orange-900/20 border-orange-500'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">🔍</span>
                              <div>
                                <h4 className="font-semibold">Direct Verification Result</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  {site.directVerification.note}
                                </p>
                              </div>
                            </div>
                            {site.directVerification.profileUrl && (
                              <Button
                                size="sm"
                                color="primary"
                                variant="flat"
                                as="a"
                                href={site.directVerification.profileUrl}
                                target="_blank"
                                startContent={<span>🔗</span>}
                              >
                                View Profile
                              </Button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Search Strategies */}
                      {site.searchStrategies && site.searchStrategies.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                            <span>🎯</span>
                            Search Strategies ({site.searchStrategies.length})
                          </h4>
                          <div className="grid gap-3">
                            {site.searchStrategies.map((strategy, stratIdx) => (
                              <div key={stratIdx} className="border rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex items-center gap-3">
                                    <Chip 
                                      size="sm" 
                                      variant="dot" 
                                      color={getEffectivenessColor(strategy.effectiveness)}
                                    >
                                      {strategy.effectiveness}
                                    </Chip>
                                    <h5 className="font-medium">{strategy.type}</h5>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      isIconOnly
                                      onClick={() => navigator.clipboard.writeText(strategy.searchUrl)}
                                      title="Copy search URL"
                                    >
                                      📋
                                    </Button>
                                    <Button
                                      size="sm"
                                      color="primary"
                                      variant="flat"
                                      as="a"
                                      href={strategy.searchUrl}
                                      target="_blank"
                                      startContent={<span>🔍</span>}
                                    >
                                      Execute Search
                                    </Button>
                                  </div>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                  {strategy.description}
                                </p>
                                <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded text-xs font-mono break-all">
                                  {strategy.query}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Search Tips */}
                      {site.searchTips && (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                          <div className="flex items-start gap-2">
                            <span className="text-lg">💡</span>
                            <div>
                              <h4 className="font-medium text-yellow-700 dark:text-yellow-300">
                                Search Tips for {site.site}
                              </h4>
                              <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                                {site.searchTips}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </Tab>
                ))}
              </Tabs>
            </ModalBody>

            <ModalFooter>
              <div className="flex justify-between items-center w-full">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  💡 Tip: Click individual search buttons to execute Google searches in new tabs
                </div>
                <div className="flex gap-2">
                  <Button color="danger" variant="flat" onPress={onClose}>
                    Close
                  </Button>
                </div>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default GoogleDorkingModal; 